import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Alert,
  IconButton,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../../../config/firebase";
import { faChartArea } from "@fortawesome/free-solid-svg-icons";
import ModalDialogTitle from "../../common/ModalDialogTitle";
import CcpSchoolBreadCrumbs from "../../Layout/CcpSchoolBreadCrumbs";
import CoursePlanningForm from "./inc/CoursePlanningForm";
import { SchoolContext } from "../../../config/SchoolContext";
import { useAuth } from "../../../config/AuthContext";
import useSendSchoolNotification from "../../../config/sendSchoolNotificationHook";
import useCoursePlanningLogger from "../../../hooks/useCoursePlanningLogger";
import useCoursePlanningBetaAccess from "../../../hooks/useCoursePlanningBetaAccess";
import SubmitFeedbackButton from "./inc/SubmitFeedbackButton";
import useFetchXCSI from "../../../hooks/useFetchXCSI";
import useFirestore from "../../../config/fireBaseHook";
import XcsiChangeRequest from "../../CcpAdmin/CcpAdminXcsi/XcsiChangeRequest";
import { formatDateTime } from "../../../utils/formatDate";

// Helper function to convert dayjs objects to ISO strings for Firestore compatibility
const convertDayjsToISO = (obj) => {
  if (!obj) return obj;

  if (obj.$isDayjsObject) {
    return obj.toISOString();
  }

  if (Array.isArray(obj)) {
    return obj.map(convertDayjsToISO);
  }

  if (typeof obj === "object") {
    const converted = {};
    for (const [key, value] of Object.entries(obj)) {
      converted[key] = convertDayjsToISO(value);
    }
    return converted;
  }

  return obj;
};

const CcpSchoolCoursePlanning = () => {
  const navigate = useNavigate();
  const pageName = "Course Planning";
  const icon = faChartArea;
  const { schoolId, schoolInfo } = useContext(SchoolContext);
  const { currentUser } = useAuth();
  const { sendNotification } = useSendSchoolNotification();
  const { logEvent, getSchoolName } = useCoursePlanningLogger();
  const { enabled: isSchoolBetaEnabled, loading: betaAccessLoading } =
    useCoursePlanningBetaAccess();
  const formId = "ix25Z19hd05vXVjPivyG"; // Document ID in Firestore for forms configuration

  // XCSI data for the locked-form view
  const { data: xcsiData, loading: xcsiLoading } = useFetchXCSI();
  const { data: buildingData } = useFirestore("CcpBuildingMgt");

  // Look up the bldg code for this school from CcpBuildingMgt.
  // SchoolContext only ever exposes `schoolId` (it has no `schoolInfo` —
  // that was always undefined) — `schoolId` is correct for both a direct
  // school login and a district user who picked one of their schools
  // (DistrictSchoolsList sets it via setSchoolId before navigating here).
  // getSchoolName resolves the name for that id via useSchoolDropdown,
  // same lookup CoursePlanningForm already uses to name-tag saved rows.
  const schoolName = getSchoolName(schoolId);
  const schoolBldgCode =
    (buildingData || []).find((b) => b.highschool === schoolName)?.bldg || null;

  // State for form configuration
  const [formData, setFormData] = useState({
    coursePlanning: "",
  });
  const [loadingForms, setLoadingForms] = useState(true);
  const [formsError, setFormsError] = useState(null);

  // ── School activity logs ──────────────────────────────────────────────────
  const [schoolLogs, setSchoolLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logModal, setLogModal] = useState({ open: false, category: null });
  const [changesSaved, setChangesSaved] = useState(false);

  const handleChangesSaved = () => {
    setChangesSaved(true);
    setTimeout(() => setChangesSaved(false), 15000);
  };

  // Load form configuration from Firestore - only once on mount
  useEffect(() => {
    const loadFormData = async () => {
      try {
        setLoadingForms(true);
        const docRef = doc(db, "forms", formId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setFormData(docSnap.data());
        } else {
          console.error("No such document!");
        }
      } catch (error) {
        console.error("Failed to fetch forms data:", error);
        setFormsError(error.message);
      } finally {
        setLoadingForms(false);
      }
    };

    loadFormData();
  }, []);

  // Fetch school's activity logs from CoursePlanningLog
  useEffect(() => {
    const fetchSchoolLogs = async () => {
      const sid = schoolId;
      if (!sid) return;
      try {
        setLogsLoading(true);
        const q = query(
          collection(db, "CoursePlanningLog"),
          where("schoolId", "==", sid),
          orderBy("timestamp", "desc"),
        );
        const snap = await getDocs(q);
        setSchoolLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Error fetching school logs:", err);
      } finally {
        setLogsLoading(false);
      }
    };
    fetchSchoolLogs();
  }, [schoolId, schoolInfo]);

  // Check if the form is visible (both school authorization and date check)
  // (schoolName is already declared above with the XCSI bldg lookup)

  // School authorization now comes from CcpBetaTesting (managed on the
  // admin "Beta Testing" page under the Course Planning breadcrumb) instead
  // of a hardcoded school-name list.
  const isSchoolAuthorized = isSchoolBetaEnabled;

  // Check if form should be locked (past the hide date but still accessible for viewing)
  const isFormLocked =
    formData.coursePlanning && new Date() > new Date(formData.coursePlanning);

  // Form is visible if school is authorized (regardless of date for now)
  const isFormVisible = isSchoolAuthorized;

  // Show loading state while checking forms configuration or beta access
  if (loadingForms || betaAccessLoading) {
    return (
      <Box sx={{ marginTop: "100px", padding: 3 }}>
        <CcpSchoolBreadCrumbs pageName={pageName} icon={icon} />
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Show error state if forms configuration failed to load
  if (formsError) {
    return (
      <Box sx={{ marginTop: "100px", padding: 3 }}>
        <CcpSchoolBreadCrumbs pageName={pageName} icon={icon} />
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Error Loading Form Configuration
          </Typography>
          <Typography>
            Unable to load form settings. Please try again later.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // If not visible (either not authorized or form is locked), show appropriate message
  if (!isFormVisible) {
    return (
      <Box sx={{ marginTop: "100px", padding: 3 }}>
        <CcpSchoolBreadCrumbs pageName={pageName} icon={icon} />
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Form Not Available
          </Typography>
          <Typography>
            The Course Planning form is currently not available. This could be
            because:
            <ul style={{ marginTop: "8px", marginBottom: "0" }}>
              <li>Your school does not have access to this feature</li>
              <li>The form has been locked by the administrator</li>
              <li>The submission deadline has passed</li>
            </ul>
            Please contact the administrator if you believe this is an error.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const handleBack = () => {
    navigate("/CcpSchoolRestrictedCourses");
  };

  // Remove excessive debug logging
  // Only log once for the first row, and only in development
  const handleSubmit = async (formDataRows) => {
    try {
      // Check if this is a change request submission (when form is locked)
      const isChangeRequest =
        isFormLocked && formDataRows.some((row) => row.changeAction);

      if (isChangeRequest) {
        // Save change requests to a separate collection
        const savePromises = formDataRows.map(async (formData) => {
          // Convert all dayjs objects to ISO strings recursively
          const convertedFormData = convertDayjsToISO(formData);

          const changeRequestData = {
            ...convertedFormData,
            schoolId: schoolId,
            schoolName: schoolName || "Unknown School",
            submittedBy: currentUser?.email || "Unknown User",
            submittedAt: new Date().toISOString(),
            status: "pending",
            // Ensure isPending is always a boolean value
            isPending: true,
          };
          return addDoc(
            collection(db, "CoursePlanningChangeRequests"),
            changeRequestData,
          );
        });

        await Promise.all(savePromises);

        // Also update the original CoursePlanningForm records with change request info
        const updateOriginalPromises = formDataRows.map((formData) => {
          if (formData.id && typeof formData.id === "string") {
            const docRef = doc(db, "CoursePlanningForm", formData.id);
            const updateData = {
              changeAction: formData.changeAction,
              specificChange: formData.specificChange,
              isPending: true,
              updatedAt: new Date().toISOString(),
              updatedBy: currentUser?.uid,
            };
            return updateDoc(docRef, updateData);
          }
        });

        await Promise.all(updateOriginalPromises.filter(Boolean));

        // Log each change request to the activity log for admin visibility
        for (const formData of formDataRows) {
          const changeDescription = `Change Request: ${
            formData.changeAction
          } - ${formData.specificChange} for course "${
            formData.courseName || "N/A"
          }"`;
          await logEvent(
            "Change Request",
            changeDescription,
            formData,
            null,
            schoolId,
          );
        }

        // Send notification for change requests
        if (formDataRows.length > 0) {
          const changesSummary = formDataRows
            .map(
              (row) =>
                `${row.courseName || "N/A"} - Action: ${
                  row.changeAction || "N/A"
                } - Details: ${row.specificChange || "No details"}`,
            )
            .join("<br />");

          const emailBodyContent = [
            `<strong>School Name:</strong> ${schoolName || "N/A"}`,
            `<strong>Number of Change Requests:</strong> ${formDataRows.length}`,
            `<br /><strong>Change Requests Summary:</strong><br />`,
            changesSummary,
          ].join("<br />");

          const notificationData = {
            pathwayEmail: schoolInfo?.pathway_email || "ccplus@sinclair.edu",
            opEmail: schoolInfo?.op_email || "ccplus@sinclair.edu",
            schoolId: schoolId || "1",
            fromName: schoolName || "School User",
            schoolName: schoolName || "Unknown School",
            emailBodyContent: emailBodyContent,
          };

          try {
            await sendNotification(
              notificationData,
              "Course Planning Change Request",
              "CoursePlanningChangeRequest",
            );
            console.log(
              "Course planning change request notification sent successfully.",
            );
          } catch (notificationError) {
            console.error(
              "Error sending change request notification:",
              notificationError,
            );
            // Don't fail the entire submission if notification fails
          }
        }
      } else {
        // Regular course planning submission

        // Resolve school name/id once to avoid inconsistencies. schoolId
        // is the school the user is actually managing right now — for a
        // district user that's whichever school they picked in
        // DistrictSchoolsList, not their own login profile — and
        // schoolName (getSchoolName(schoolId), declared above) is looked
        // up from that same id, so the two always agree.
        const resolvedSchoolName = schoolName || "Unknown School";
        const resolvedSchoolId = schoolId || "Unknown SchoolId";

        // Log resolved school info for debugging (single concise log)
        console.log(
          "Submitting CoursePlanningForm - resolvedSchoolName:",
          resolvedSchoolName,
          "resolvedSchoolId:",
          resolvedSchoolId,
        );

        const savePromises = formDataRows.map(async (formData, idx) => {
          // Defensive: ensure we are not mutating the original formData
          const formDataCopy = { ...formData };
          // Add resolved schoolName and schoolId to each formData before saving
          formDataCopy.schoolName = resolvedSchoolName;
          formDataCopy.schoolId = resolvedSchoolId;

          // Convert any dayjs objects to ISO strings to be safe
          const converted = convertDayjsToISO(formDataCopy);

          // Only log the first row's payload to avoid noise
          if (idx === 0)
            console.log("Saving to CoursePlanningForm (sample):", converted);

          return addDoc(collection(db, "CoursePlanningForm"), converted);
        });

        await Promise.all(savePromises);

        // Send notification if there's course planning data
        if (formDataRows.length > 0) {
          // Create a summary of the course planning data
          const courseSummary = formDataRows
            .map(
              (row) =>
                `${row.courseName || "N/A"} - ${
                  row.numberOfSections || 0
                } sections (${row.instructorType || "N/A"})`,
            )
            .join("<br />");

          const emailBodyContent = [
            `<strong>School Name:</strong> ${schoolName || "N/A"}`,
            `<strong>Number of Courses:</strong> ${formDataRows.length}`,
            `<br /><strong>Course Planning Summary:</strong><br />`,
            courseSummary,
          ].join("<br />");

          const notificationData = {
            pathwayEmail: schoolInfo?.pathway_email || "ccplus@sinclair.edu",
            opEmail: schoolInfo?.op_email || "ccplus@sinclair.edu",
            schoolId: schoolId || "1",
            fromName: schoolName || "School User",
            schoolName: schoolName || "Unknown School",
            emailBodyContent: emailBodyContent,
          };

          try {
            await sendNotification(
              notificationData,
              "Course Planning Submission",
              "CoursePlanning",
            );
            console.log("Course planning notification sent successfully.");
          } catch (notificationError) {
            console.error(
              "Error sending course planning notification:",
              notificationError,
            );
            // Don't fail the entire submission if notification fails
          }
        }
      }

      // Don't navigate back, let the form handle the success message
      // The form will show its own snackbar with "Course Planning Updated"
    } catch (error) {
      console.error("Error saving course planning data:", error);
      // Re-throw the error so the form can handle it
      throw error;
    }
  };

  const additionLogs = schoolLogs.filter((l) => l.type === "Addition");
  const changeLogs = schoolLogs.filter(
    (l) =>
      l.type === "Change" ||
      l.type === "Change Request" ||
      l.type === "Course Updated",
  );
  const cancelLogs = schoolLogs.filter(
    (l) =>
      l.type === "Course Cancel Requested" ||
      l.type === "Course Canceled Complete" ||
      l.type === "Cancel Request" ||
      l.type === "Cancel Course",
  );

  const categoryLogsMap = {
    Additions: additionLogs,
    Changes: changeLogs,
    Cancellations: cancelLogs,
  };
  const modalLogs = logModal.category ? categoryLogsMap[logModal.category] : [];

  return (
    <Box
      sx={{
        marginTop: "100px",
        width: "100vw",
        maxWidth: "100vw",
        paddingLeft: 2,
        paddingRight: 2,
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ width: "100%" }}>
        <CcpSchoolBreadCrumbs
          pageName={pageName}
          icon={icon}
          headerAction={
            <SubmitFeedbackButton
              schoolName={schoolName}
              userName={currentUser?.name}
              userEmail={currentUser?.email}
              location="Course Planning"
            />
          }
        />

        {/* ── Activity summary chips ──────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            mb: 2,
            mt: 2,
            flexWrap: "wrap",
          }}
        >
          {logsLoading ? (
            <CircularProgress size={16} />
          ) : (
            <>
              <Chip
                label={`Additions: ${additionLogs.length}`}
                onClick={() =>
                  setLogModal({ open: true, category: "Additions" })
                }
                sx={{
                  backgroundColor: "#dcfce7",
                  color: "#15803d",
                  fontWeight: 600,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#bbf7d0" },
                }}
              />
              <Chip
                label={`Changes: ${changeLogs.length}`}
                onClick={() => setLogModal({ open: true, category: "Changes" })}
                sx={{
                  backgroundColor: "#fff7ed",
                  color: "#c2410c",
                  fontWeight: 600,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#fed7aa" },
                }}
              />
              <Chip
                label={`Cancellations: ${cancelLogs.length}`}
                onClick={() =>
                  setLogModal({ open: true, category: "Cancellations" })
                }
                sx={{
                  backgroundColor: "#fee2e2",
                  color: "#b91c1c",
                  fontWeight: 600,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#fecaca" },
                }}
              />
            </>
          )}
        </Box>

        <CoursePlanningForm
          onSubmit={handleSubmit}
          onCancel={handleBack}
          isFormLocked={isFormLocked}
        />

        {/* When the form is locked, show the school's XCSI course data with change request capability */}
        {isFormLocked && (
          <Box sx={{ mt: 4 }}>
            {changesSaved && (
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  backgroundColor: "#16a34a",
                  color: "#fff",
                  "& .MuiAlert-icon": { color: "#fff" },
                  fontWeight: 600,
                }}
              >
                Your changes have been sent successfully.
              </Alert>
            )}
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Your Current Course Sections
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The course planning form is locked. Use the table below to submit
              change requests for your courses. Select a Change Action and
              describe the change, then click <strong>Save Changes</strong>.
            </Typography>
            <XcsiChangeRequest
              data={xcsiData}
              loading={xcsiLoading}
              bldg={schoolBldgCode}
              schoolId={schoolId}
              schoolName={schoolName}
              currentUser={currentUser}
              onSaveSuccess={handleChangesSaved}
            />
          </Box>
        )}
      </Box>

      {/* ── Activity log modal ────────────────────────────────────────────── */}
      <Dialog
        open={logModal.open}
        onClose={() => setLogModal({ open: false, category: null })}
        maxWidth="sm"
        fullWidth
      >
        <ModalDialogTitle
          onClose={() => setLogModal({ open: false, category: null })}
        >
          {logModal.category}
        </ModalDialogTitle>
        <DialogContent dividers>
          {modalLogs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No {logModal.category?.toLowerCase()} logged yet.
            </Typography>
          ) : (
            modalLogs.map((log, idx) => (
              <Box key={log.id}>
                {idx > 0 && <Divider sx={{ my: 1.5 }} />}
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(log.timestamp) || "—"}
                </Typography>

                {/* Additions & Cancellations: description only */}
                {(logModal.category === "Additions" ||
                  logModal.category === "Cancellations") && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {log.description || "—"}
                  </Typography>
                )}

                {/* Changes: changeAction, description, specificChange */}
                {logModal.category === "Changes" && (
                  <Box sx={{ mt: 0.5 }}>
                    {log.changeAction && (
                      <Box sx={{ mb: 0.75 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            textTransform: "uppercase",
                            color: "text.secondary",
                          }}
                        >
                          Change Action
                        </Typography>
                        <Typography variant="body2">
                          {log.changeAction}
                        </Typography>
                      </Box>
                    )}
                    {log.description && (
                      <Box sx={{ mb: 0.75 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            textTransform: "uppercase",
                            color: "text.secondary",
                          }}
                        >
                          Description
                        </Typography>
                        <Typography variant="body2">
                          {log.description}
                        </Typography>
                      </Box>
                    )}
                    {log.specificChange && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            textTransform: "uppercase",
                            color: "text.secondary",
                          }}
                        >
                          Specific Change
                        </Typography>
                        <Typography variant="body2">
                          {log.specificChange}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ))
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CcpSchoolCoursePlanning;
